import { h, Component } from 'preact';
import cx from 'classnames';
import dlv from 'dlv';
import { Text, MarkupText } from 'preact-i18n';
import { Button } from '@cube/blocks';
import callWith from '@os/call-with';
import { trackEvent, INPUT_KEYS, DIMENSION, EVENT, INTERACTION } from '@cube/track';
import { deleteCookie } from '@cube/privacy-utils';
import { configure } from '../../config';
import style from './style';
import { CcpaButtons } from '../ccpa-buttons';


// list of personal cookies
// format of each entry is name, description, value formatter function
// missing value formatter will simply output the value
const PERSONAL_COOKIES = [
	{
		name: 'ccpaOptOut',
		description: 'California Consumer Privacy Act values'
	},
	{
		name: 'gpp',
		description: 'Global Privacy Platform values'
	},
	{
		name: 'privacyInfo',
		description: 'Information for privacy settings',
		formatterFn: safeUrlEncodedJSONParse,
		source: 'Automatically from location of IP and privacy signals'
	},
	{
		name: 'privacyInfoChanged',
		description: 'Indicates if privacy settings should be reprocessed',
		source: 'Automatically from location of IP and privacy signals'
	},
	{
		name: 'pubUID',
		description: 'A unique identifier used for advertising and analytics',
		source: 'Automatically generated from a random number'
	}
];

// Cookies that are not used any more.  Most likely old gen4 cookies
const DEAD_COOKIES = [
	'temporalchip',
	'eternalchip',
	'synaff',
	'session'
];

function safeUrlEncodedJSONParse(v) {
	try {
		return JSON.parse(decodeURIComponent(v));
	}
	catch (e) {
		return v;
	}
}

function safeJSONParse(v) {
	try {
		return JSON.parse(v);
	}
	catch (e) {
		return v;
	}
}


// list of personal local storage entries
// format of each entry is name, description, value formatter function
// missing value formatter will simply output the value
const PERSONAL_LOCAL_STORAGE = [
	//weather widget and anything else that uses location
	{
		name: 'location-model',
		description: 'Location preferences',
		formatterFn: safeJSONParse
	},
	//finance widget
	{
		name: 'finance-components.stock-recently-viewed',
		description: 'Recently Viewed Stock Quotes',
		formatterFn: safeJSONParse
	},
	{
		name: 'sync-user-preferences',
		description: 'General User preferences',
		formatterFn: safeJSONParse
	},
	{
		name: 'alerts.dismissed',
		description: 'Information on browser based alerts you have dismissed',
		formatterFn: safeJSONParse
	},
	{
		name: 'serpSafeSearch',
		description: 'Safe searches enabled'
	}
];

const DELETING_DATA = 2;
const DELETED_DATA = 3;
const DELETE_FAILED = 4;
const HAS_DATA = 5;

const STATUS_TO_TEXT_ID = [
	'statusChecking',
	'statusNoData',
	'statusDeleting',
	'statusDeleted',
	'statusFailed'
];

const TYPE_COOKIE=1;
const TYPE_LOCALSTORAGE=2;

export class CcpaDataWidget extends Component {

	updateStatus = (name, status) => {
		let userData = { ...this.state.userData };
		userData[name].status = status;
		this.setState({ userData });
	}

	deleteData = (name) => {
		trackEvent( this.context, {
			[INPUT_KEYS.action]: EVENT.click,
			[DIMENSION.componentpath]: 'CcpaData',
			[DIMENSION.interaction]: INTERACTION.delete,
			[DIMENSION.text]: name
		});

		if (this.state.userData[name].type === TYPE_COOKIE) {
			deleteCookie(name);
			this.updateStatus(name, DELETED_DATA);
			return;
		}
		else if (this.state.userData[name].type === TYPE_LOCALSTORAGE) {
			localStorage.removeItem(name);
			this.updateStatus(name, DELETED_DATA);
			return;
		}

		this.updateStatus(name, DELETING_DATA);
	}

	getJustData = (content) => dlv(content, 'result', 'No Data');

	getDownloadData = () => {
		const rows = [
			['Name', 'Source', 'Description', 'Value'],
			...Object.keys(this.state.userData).reduce((acc, k) => {
				let obj = this.state.userData[k];
				//ignore deleted fields for the download
				if (obj.status === HAS_DATA) {
					acc.push([obj.name||k, obj.source, obj.description, JSON.stringify(obj.value)]);
				}
				return acc;
			}, [])
		];

		let csvContent = 'data:text/tsv;charset=utf-8,'
			+ rows.map(e => e.join('\t')).join('\n');

		return encodeURI(csvContent);
	}

	getStatus = (type) => {
		let status = this.state.userData[type].status;
		return status === HAS_DATA ?
			(
				<Button class={style.delete} type="primary" brand="primary" onClick={callWith(this.deleteData, type)} >
					<Text id="statusDeleteButton" />
				</Button>
			) :
			<Text id={STATUS_TO_TEXT_ID[this.state.userData[type].status]} />;
	}

	onDownloadClick = () => {
		trackEvent( this.context, {
			[INPUT_KEYS.action]: EVENT.click,
			[DIMENSION.componentpath]: 'CcpaData',
			[DIMENSION.interaction]: INTERACTION.download
		});
	}

	componentWillMount() {
		//Get personal data from cookies
		let cookieMap = (document.cookie || '').split(';').reduce((cm, kv) => {
			let [ name, value ] = kv.split('=');
			name = name.trim();
			cm[name] = value;

			// Delete if dead cookie
			if (DEAD_COOKIES.includes(name)) {
				deleteCookie(name);
			}

			return cm;
		}, {});

		let userData = {};

		//Add data for present personal cookies and localstorage
		[
			[PERSONAL_COOKIES, TYPE_COOKIE, 'cookie'],
			[PERSONAL_LOCAL_STORAGE, TYPE_LOCALSTORAGE, 'local storage']
		].forEach(([metaArr, storageType, storageStr]) => {
			metaArr.forEach(({ name, description, formatterFn, source }) => {
				let value = storageType === TYPE_COOKIE ? cookieMap[name] : localStorage.getItem(name);
				let keyExists = storageType === TYPE_COOKIE ? name in cookieMap : value !== null;
				if (!keyExists) return; //skip missing entries
				userData[name] = {
					name: `${name} (${storageStr})`,
					description,
					type: storageType,
					source: source || 'User Interaction',
					status: HAS_DATA,
					value: formatterFn ? formatterFn(value) : value
				};
			});
		});

		this.setState({ userData });
	}

	renderOriginalVersion({ buttonLinks, intlFields, mockDataInjectionComponent, userData }) {
		return (
			<div class={style.ccpa}>
				<div class={style.title}><Text id="dataTitle" /></div>
				<div class={style.description}><Text id="dataDescription1" fields={intlFields} /></div>
				<div class={style.description}><Text id="dataDescription2" fields={intlFields} /></div>
				<div class={style.description}><MarkupText id="dataDescription3" fields={intlFields} /></div>
				<div class={style.sectionTitle}><Text id="dataSoldTitle" /></div>
				<div class={style.sectionDescription}><MarkupText id="dataSoldDescription1" fields={intlFields} /></div>
				<div class={style.sectionDescription}><MarkupText id="dataSoldDescription2" fields={intlFields} /></div>

				{this.renderUserDataSection({ mockDataInjectionComponent, userData })}

				<div class={style.sectionTitle}><Text id="linksTitle" /></div>
				<CcpaButtons links={buttonLinks} />
			</div>);
	}

	render20201123Version({ buttonLinks, clientCCPALink, intlFields, mockDataInjectionComponent, userData, foo }) {
		return (
			<div class={style.ccpa}>
				<div class={style.title}><Text id="20201123.dataTitle" /></div>
				<div class={style.description}><Text id="20201123.dataDescription1" fields={intlFields} /><MarkupText id={`20201123.descriptionCoveredInteractionsWith${dlv(clientCCPALink, 'url') ? '' : 'out'}CCPAResource`} fields={intlFields} /></div>
				<div class={style.description}><MarkupText id="20201123.dataDescription2" fields={intlFields} /></div>
				<div class={style.description}><MarkupText id="20201123.dataDescription3" fields={intlFields} /></div>
				<div class={style.sectionTitle}><Text id="20201123.dataSoldTitle" /></div>
				<div class={style.sectionDescription}><MarkupText id="20201123.dataSoldDescription1" fields={intlFields} /></div>
				<div class={style.sectionDescription}><MarkupText id="20201123.dataSoldDescription2" fields={intlFields} /></div>

				{this.renderUserDataSection({ mockDataInjectionComponent, userData })}

				<div class={style.sectionTitle}><Text id="linksTitle" /></div>
				<CcpaButtons links={buttonLinks} />
			</div>);
	}

	renderUserDataSection({ mockDataInjectionComponent, userData }) {
		return (<div class={style.data}>
			<div class={style.sectionTitle}><Text id="dataUserTitle" /></div>
			{ mockDataInjectionComponent }
			<div class={style.description}><Text id="dataUserDescription" /></div>
			<div class={style.tableContainer}>
				<table>
					<tr>
						<th>Name</th>
						<th>Source</th>
						<th>Status</th>
						<th>Description</th>
						<th>Value</th>
					</tr>
					{
						Object.keys(userData).map((k) =>
							(<tr>
								<td>{userData[k].name || k}</td>
								<td>{userData[k].source}</td>
								<td  class={cx([DELETED_DATA, DELETE_FAILED].includes(userData[k].status) && style.deleted)}>{this.getStatus(k)}</td>
								<td>{userData[k].description}</td>
								<td><pre>{JSON.stringify(userData[k].value, null, 2)}</pre></td>
							</tr>)
						)
					}
				</table>
			</div>
			<a href={this.getDownloadData()} download="my_data.tsv">
				<Button class={style.download} type="primary" brand="primary" onClick={callWith(this.onDownloadClick)}>
					<Text id="downloadButton" />
				</Button>
			</a>
		</div>);
	}

	render({ links, clientCCPALink, clientDisplayName, mockDataInjectionComponent, overrideVersion }, { userData }) {
		let buttonLinks = [links.main, links.partners, links.privacy],
			hostname = window.location.hostname;
		let intlFields = { hostname, ccpaLinks: { ...links, clientCCPA: clientCCPALink }, clientDisplayName };

		return overrideVersion === '20201123' ?
			this.render20201123Version({ buttonLinks, intlFields, clientCCPALink, mockDataInjectionComponent, userData }) :
			this.renderOriginalVersion({ buttonLinks, intlFields, mockDataInjectionComponent, userData });
	}
}

export const CcpaData = (configure({
	overrideVersion: 'privacyWidgetsConfig.overrideVersion',
	links: 'privacyWidgetsConfig.ccpa.links',
	clientDisplayName: 'clientDisplayName',
	clientCCPALink: 'privacyWidgetsConfig.ccpa.links.clientCCPA' //break out separately for easier toggling/mocking of it in DEMO
})((props) =>  (
	<CcpaDataWidget {...props} />
)));
